import React from 'react';
import {Breakpoints} from '@volkswagen-onehub/components-core';

import {isInBrowser} from '../../utils/browser/isInBrowser';

export enum QueryNames {
    isMobile = 'isMobile',
    isMobileLandscape = 'isMobileLandscape',
    isTablet = 'isTablet',
    isLaptop = 'isLaptop',
    isDesktop = 'isDesktop',
    reducedAnimation = 'reducedAnimation'
}

export type QueryResults = Record<QueryNames, boolean>;
export type QueryStrings = Record<QueryNames, string>;

// 1) Raw media query strings
export const globalQueries: QueryStrings = {
    isMobile: `(max-width: ${Breakpoints.b560}px)`,
    isMobileLandscape: `(min-height: 321px) and (max-height: ${Breakpoints.b560}px) and (orientation: landscape)`,
    isTablet: `(min-width: ${Breakpoints.b560 + 1}px) and (max-width: 1024px) and (min-height: 481px) and (pointer: coarse)`,
    isLaptop: `(min-width: ${Breakpoints.b960}px) and (max-width: ${Breakpoints.b1280 - 1}px) and (pointer: fine)`,
    isDesktop: `(min-width: ${Breakpoints.b1280}px)`,
    reducedAnimation: `(prefers-reduced-motion: reduce)`
};

// SSR fallback when matchMedia is unavailable
export const ssrFallbackQueries: QueryResults = {
    isMobile: true,
    isMobileLandscape: false,
    isTablet: false,
    isLaptop: false,
    isDesktop: false,
    reducedAnimation: false
};

// 2) Pure helpers for easier testing
export function buildMediaQueryLists(
    queries: QueryStrings
): Record<QueryNames, MediaQueryList> {
    // always start with an empty record
    const lists = {} as Record<QueryNames, MediaQueryList>;

    // if we’re not in a real browser or matchMedia is missing, just bail
    if (
        typeof window === 'undefined' ||
        typeof window.matchMedia !== 'function'
    ) {
        return lists;
    }

    // otherwise fill it in
    for (const key of Object.keys(queries) as QueryNames[]) {
        lists[key] = window.matchMedia(queries[key]);
    }

    return lists;
}

export function getMatchesFromLists(
    lists: Record<QueryNames, MediaQueryList>
): QueryResults {
    const result = {} as QueryResults;
    for (const key in lists) {
        result[key as QueryNames] = lists[key as QueryNames].matches;
    }
    return result;
}

export function subscribeToLists(
    lists: Record<QueryNames, MediaQueryList>,
    onChange: (name: QueryNames, matches: boolean) => void
): Record<QueryNames, (evt: MediaQueryListEvent) => void> {
    const handlers = {} as Record<
        QueryNames,
        (evt: MediaQueryListEvent) => void
    >;
    for (const key in lists) {
        const name = key as QueryNames;
        handlers[name] = (evt: MediaQueryListEvent) =>
            onChange(name, evt.matches);
        lists[name].addEventListener('change', handlers[name]);
    }
    return handlers;
}

export function unsubscribeFromLists(
    lists: Record<QueryNames, MediaQueryList>,
    handlers: Record<QueryNames, (evt: MediaQueryListEvent) => void>
): void {
    for (const key in handlers) {
        const name = key as QueryNames;
        lists[name].removeEventListener('change', handlers[name]);
    }
}

// 3) Hook implementation
export function useMediaQueries(): QueryResults {
    const queries = React.useMemo(() => globalQueries, []);

    // Build lists in browser, otherwise fallback
    const [matches, setMatches] = React.useState<QueryResults>(() =>
        !isInBrowser()
            ? ssrFallbackQueries
            : getMatchesFromLists(buildMediaQueryLists(queries))
    );

    // Keep refs to lists and handlers
    const listsRef = React.useRef<Record<QueryNames, MediaQueryList> | null>(
        null
    );
    const handlersRef = React.useRef<
        Record<QueryNames, (evt: MediaQueryListEvent) => void>
    >({} as any);

    React.useEffect(() => {
        if (!isInBrowser()) return;

        // 1) create and store lists
        listsRef.current = buildMediaQueryLists(queries);

        // 2) subscribe
        handlersRef.current = subscribeToLists(
            listsRef.current,
            (name, matches) => setMatches(prev => ({...prev, [name]: matches}))
        );

        // 3) initial sync
        setMatches(getMatchesFromLists(listsRef.current));

        // 4) cleanup
        return () => {
            if (!listsRef.current) return;
            unsubscribeFromLists(listsRef.current, handlersRef.current);
        };
    }, [queries]);

    return matches;
}
